import { AiSuggestSummaryBanner } from '@/components/AiSuggestSummaryBanner';
import { usePaymentDetail } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import { ApplicationContent } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/ApplicationContent';
import { TransactionsDetailSection } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail';
import {
  SCROLL_AREA_ATTRIBUTE,
  useHandleAnchor,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/anchor';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import type { ReportInferencesMismatchViewData } from '@/hooks/report_inferences/useReportInferencesMismatchCaptions';
import { Anchor, Loading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useRef, useState } from 'react';
import { BasicInfoSection } from './BasicInfoSection';
import { useApprovingInvoiceReportSummaryData } from './hooks';
import { JournalInfo } from './JournalInfo';
import styles from './styles.module.scss';
import { FooterAction } from './FooterAction';
const cx = classnames.bind(styles);

type LeftContentProps = {
  id: string;
  onClose: () => void;
};

const InnerLeftContent: FC<LeftContentProps> = ({ id, onClose }) => {
  const { data } = useApprovingInvoiceReportSummaryData(id, onClose);
  const { isRightPaneCollapsed, titleInfo } = usePaymentDetail();
  const [mismatchViewData, setMismatchViewData] = useState<
    ReportInferencesMismatchViewData | undefined
  >();

  // NA-5579: own flag, separate from `approval-drawer-view-optimization`
  // (the table/card layout-switch flag), which is being removed this sprint.
  const isAnchorEnabled = useGetFlags('APPROVAL_DRAWER_ANCHOR_NAV');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRootRef = useRef<HTMLDivElement>(null);
  const { anchorItems, getContainer, getCurrentAnchor, handleAnchorClick } =
    useHandleAnchor({
      scrollContainerRef,
      sectionRootRef,
      reportId: id,
      enabled: isAnchorEnabled,
    });

  const sections = (
    <>
      <AiSuggestSummaryBanner
        invoiceAiSuggestion={data?.invoice_ai_suggestion}
      />
      <BasicInfoSection
        data={data}
        paymentTotalMismatchCaption={mismatchViewData?.paymentTotalCaption}
      />
      <ApplicationContent
        reportId={id}
        paymentTotalAmount={data?.total_value}
        onMismatchViewDataChange={setMismatchViewData}
      />
      <TransactionsDetailSection reportId={id} />
      <JournalInfo reportId={id} />
      <FooterAction
        is_report_approval={titleInfo?.is_report_approval}
        is_approvable={titleInfo?.is_approvable}
        reportId={id}
      />
    </>
  );

  if (!isAnchorEnabled) {
    return (
      <div
        className={cx('left-content', { 'full-width': isRightPaneCollapsed })}
      >
        {sections}
      </div>
    );
  }

  return (
    <div
      className={cx('left-content', 'with-anchor', {
        'full-width': isRightPaneCollapsed,
      })}
    >
      <Anchor
        affix={false}
        items={anchorItems}
        getContainer={getContainer}
        getCurrentAnchor={getCurrentAnchor}
        onClick={handleAnchorClick}
        className={cx('anchor-rail')}
        containerClassName={cx('anchor-layout')}
        contentClassName={cx('anchor-content')}
      >
        {/*
          The scroll lives here rather than on `.left-content` so the rail stays
          put while the sections scroll — NA-5579 AC1 ("anchor items fixed").
        */}
        <div
          ref={scrollContainerRef}
          className={cx('scroll-area')}
          {...{ [SCROLL_AREA_ATTRIBUTE]: true }}
        >
          <div ref={sectionRootRef} className={cx('sections')}>
            {sections}
          </div>
        </div>
      </Anchor>
    </div>
  );
};

const FallBackLoading = () => {
  return (
    <div className={cx('loading-container')}>
      <Loading size='large' color='default' />
    </div>
  );
};

export const LeftContent: FC<LeftContentProps> = memo((props) => {
  return (
    <Suspense fallback={<FallBackLoading />}>
      <InnerLeftContent {...props} />
    </Suspense>
  );
});

LeftContent.displayName = 'LeftContent';
